import React from 'react';

/**
 * LoadingSpinner — the single loading indicator for Ruby Management.
 *
 * Usage:
 *   <LoadingSpinner />                                   — default block spinner
 *   <LoadingSpinner size="sm" />                         — small block spinner
 *   <LoadingSpinner size="lg" text="Loading horses..." />
 *   <LoadingSpinner inline text="Loading…" />            — row spinner for section headers / banners
 *   <LoadingSpinner inline tone="current" size="xs" />   — row spinner for buttons (inherits button colour)
 *   <LoadingSpinner fullPage />                          — centred full-page loader with the brand mark
 *
 * Theme: the ring, text and full-page brand mark follow the *app* theme via the
 * `.dark` class ThemeContext toggles on <html>. They are deliberately never keyed
 * off the OS `prefers-color-scheme`, which can disagree with the in-app choice and
 * used to make the light-mode page loader render the dark logo.
 */
const SIZE_CLASS = {
    xs: 'loading-spinner--xs',
    sm: 'loading-spinner--sm',
    md: '',
    lg: 'loading-spinner--lg',
};

const LoadingSpinner = ({
    size = 'md',
    text,
    fullPage = false,
    inline = false,
    tone = 'brand',
    className = '',
}) => {
    if (fullPage) {
        return (
            <div className={`loading-page ${className}`.trim()} role="status" aria-live="polite">
                <img
                    src="/apple-touch-icon.png"
                    alt=""
                    aria-hidden="true"
                    className="loading-page__logo loading-page__logo--light"
                />
                <img
                    src="/apple-touch-icon-dark.png"
                    alt=""
                    aria-hidden="true"
                    className="loading-page__logo loading-page__logo--dark"
                />
                <div className="loading-spinner__ring" />
                <p className="loading-spinner__text">{text || 'Loading…'}</p>
            </div>
        );
    }

    const classes = [
        'loading-spinner',
        SIZE_CLASS[size] || '',
        inline ? 'loading-spinner--inline' : '',
        tone === 'current' ? 'loading-spinner--current' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <span className={classes} role="status" aria-live="polite" aria-label={text ? undefined : 'Loading'}>
            <span className="loading-spinner__ring" />
            {text && <span className="loading-spinner__text">{text}</span>}
        </span>
    );
};

export default LoadingSpinner;
